import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { AppLink } from "@/components/app-link";

interface Breadcrumb {
  label: string;
  href?: string;
  params?: Record<string, string>;
}

interface PageHeaderProps {
  title: string;
  description?: React.ReactNode;
  breadcrumbs?: Breadcrumb[];
  actions?: React.ReactNode;
  className?: string;
}

function BreadcrumbItem({
  crumb,
  isLast,
}: {
  crumb: Breadcrumb;
  isLast: boolean;
}) {
  if (crumb.href && !isLast) {
    return (
      <AppLink
        to={crumb.href}
        params={crumb.params}
        title={crumb.label}
        className="text-muted-foreground hover:text-foreground transition-colors font-medium"
      >
        {crumb.label}
      </AppLink>
    );
  }

  return (
    <span
      className={cn(
        isLast ? "text-foreground font-semibold" : "text-muted-foreground"
      )}
      aria-current={isLast ? "page" : undefined}
    >
      {crumb.label}
    </span>
  );
}

export function PageHeader({
  title,
  description,
  breadcrumbs,
  actions,
  className,
}: PageHeaderProps) {
  return (
    <header className={cn("space-y-2 pb-4 border-b-2 border-border", className)}>
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm">
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            return (
              <span key={index} className="flex items-center gap-1.5">
                <BreadcrumbItem crumb={crumb} isLast={isLast} />
                {!isLast && (
                  <ChevronRight
                    className="size-4 text-muted-foreground"
                    aria-hidden="true"
                  />
                )}
              </span>
            );
          })}
        </nav>
      )}

      {/* Title and Actions Row */}
      <div className="flex items-start justify-between gap-4 pt-1">
        <div className="space-y-1.5">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">{title}</h1>
          {description && (
            <div className="text-sm text-muted-foreground font-medium">{description}</div>
          )}
        </div>
        {actions && <div className="flex items-center gap-3 shrink-0">{actions}</div>}
      </div>
    </header>
  );
}

/*
 * Usage Example:
 *
 * import { PageHeader } from "@/components/layout";
 * import { Button } from "@/components/ui/button";
 *
 * function DashboardPage() {
 *   return (
 *     <PageHeader
 *       title="Dashboard"
 *       description="View and manage all your submittals"
 *       breadcrumbs={[
 *         { label: "Home", href: "/" },
 *         { label: "Dashboard" },
 *       ]}
 *       actions={
 *         <Button>New Submittal</Button>
 *       }
 *     />
 *   );
 * }
 */
